import { Component, inject, OnInit, OnDestroy, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import Swal from 'sweetalert2';
import { PlatformAdminService } from '../../../core/services/platform-admin.service';

/**
 * Shown for the whole of a Solenex support session.
 *
 * The point of the impersonation design is that nothing else in the app can
 * tell it is happening — the administrator's claims and user document say they
 * are the customer's owner, so every page renders exactly what the customer
 * sees. That is what makes it useful for support, and it is also why this
 * banner has to be impossible to miss. Without it there is no visible
 * difference between looking at a customer's account and looking at your own.
 *
 * Positioned to match the trial banner it sits alongside.
 */
@Component({
    selector: 'app-impersonation-banner',
    standalone: true,
    imports: [CommonModule],
    styles: [`
      .impersonation-banner-fixed {
        position: fixed;
        top: 70px;
        left: 250px;
        right: 0;
        z-index: 1001;

        body[data-sidebar-size="sm"] & {
          left: 70px;
        }
      }
    `],
    template: `
    <div class="impersonation-banner-fixed" *ngIf="session()">
      <div class="alert alert-danger border-0 mb-0 rounded-0 d-flex align-items-center justify-content-between"
           role="alert">
        <div class="d-flex align-items-center">
          <i class="bx bx-shield-quarter font-size-18 me-2"></i>
          <span>
            <strong>Solenex support session:</strong>
            you are signed in as an owner of
            <strong>{{ session()?.orgName || session()?.orgId }}</strong>.
            Everything you do here affects this customer's account.
            <span class="ms-1 text-nowrap">Ends in {{ remainingLabel() }}.</span>
          </span>
        </div>

        <button type="button"
                class="btn btn-sm btn-light ms-3 text-nowrap"
                [disabled]="leaving()"
                (click)="exit()">
          <i class="bx bx-exit me-1"></i>
          {{ leaving() ? 'Leaving…' : 'Exit session' }}
        </button>
      </div>
    </div>
  `
})
export class ImpersonationBannerComponent implements OnInit, OnDestroy {

    private platformAdmin = inject(PlatformAdminService);

    readonly session = this.platformAdmin.impersonation;
    readonly leaving = signal(false);

    /** Ticks once a minute purely so the countdown stays honest. */
    private readonly now = signal(Date.now());
    private timer: ReturnType<typeof setInterval> | null = null;

    readonly remainingLabel = computed(() => {
        const expiresAt = this.session()?.expiresAt;
        if (!expiresAt) return 'a moment';

        const minutes = Math.max(0, Math.round((expiresAt - this.now()) / 60000));
        if (minutes < 1) return 'less than a minute';
        if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'}`;

        const hours = Math.floor(minutes / 60);
        const rest = minutes % 60;
        return rest ? `${hours}h ${rest}m` : `${hours}h`;
    });

    ngOnInit(): void {
        this.timer = setInterval(() => this.now.set(Date.now()), 60_000);
    }

    ngOnDestroy(): void {
        if (this.timer) clearInterval(this.timer);
    }

    async exit(): Promise<void> {
        this.leaving.set(true);

        try {
            // endImpersonation() reloads into the console; see the service.
            await this.platformAdmin.endImpersonation();
        } catch (error) {
            console.error('Could not end support session:', error);

            // Being stuck inside a customer's account is the failure worth
            // handling well, so say what the way out is rather than just
            // reporting that something went wrong.
            await Swal.fire({
                icon: 'error',
                title: 'Could not leave the session',
                text: 'Sign out and sign back in — that always returns you to the console.',
                confirmButtonColor: '#556ee6'
            });
        } finally {
            this.leaving.set(false);
        }
    }
}
